import type { ReactNode } from "react";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { images } from "@core/assets";

interface AuthLayoutProps {
  children: ReactNode;
  /**
   * The brand mark above the content. On by default; the frames leave it out on the link-checking
   * state (a bare card with « Vérification… »).
   */
  showLogo?: boolean;
}

/**
 * Frame for every signed-out screen (Figma, section 2 frames): a quiet page, the language pill
 * floating top right, the logo centred above the content. There is NO header bar. The content is
 * usually an `AuthCard`; a screen that is not a card (the old login and register forms, until
 * their workflows replace them) renders its own chrome in the same slot.
 *
 * Lives in core because several modules use it and it takes no domain type; cross-module imports
 * are banned, so a module-owned frame would have to be duplicated. It is the signed-out
 * counterpart of the staff frame.
 *
 * The language pill DOES belong here, unlike in the signed-in frame: a visitor has no profile
 * carrying `preferred_language`, so this pill is their only way to change it.
 *
 * Responsive: below `sm` the pill sits in the flow above the content, so it can never overlap the
 * logo on a 320px screen; from `sm` it floats at the frame's offsets (32px from the top, 40px from
 * the right) and the content is centred on the whole page height.
 */
export function AuthLayout({ children, showLogo = true }: AuthLayoutProps) {
  return (
    <div className="relative flex min-h-screen flex-col bg-surface-page">
      <div className="flex justify-end px-md pt-md sm:absolute sm:top-2xl sm:right-3xl sm:p-0">
        <LanguageSwitcher />
      </div>

      <main className="flex flex-1 flex-col items-center justify-center gap-2xl px-md py-xl sm:py-20">
        {showLogo && (
          <img
            src={images["logo-primary"]}
            alt="DZ Champions"
            className="h-8 w-auto"
          />
        )}
        {children}
      </main>
    </div>
  );
}
